import * as yup from 'yup';
import { NO_PROVIDER, RECEIPT_COLLECT_TYPE } from '../../../library/models/invoicingSettings';
import { TranslateFn } from '../../../library/validationRules';
import ResourcesIds from '../../../resources/ResourcesIds';

/** Whether a secret is already on file, which the draft itself cannot know. */
export interface InvoicingValidationContext {
    secretIsStored: boolean;
}

/** Required only once the feature is being switched on. */
const requiredWhenEnabled = (translate: TranslateFn, key: string) =>
    yup.string().defined().when('enabled', {
        is: true,
        then: (field) => field.test('required', translate(key), (value) => !!value?.trim()),
    });

/**
 * The rules `isValidInvoicingSettings` used to answer as one yes/no, restated per field.
 *
 * That change is the whole point. This panel has sixteen inputs and drew **no** field-level
 * errors at all, so an admin who ticked Enabled with one box blank got a greyed-out Save and
 * no way to find out which. Each rule now lands on the control at fault.
 *
 * Everything is only required **on the way to enabled** -- a half-filled configuration left
 * switched off saves perfectly well, which is what lets an admin do this over two sittings.
 */
export const createInvoicingSchema = (translate: TranslateFn) => yup.object({
    enabled: yup.boolean().defined(),

    provider: yup.string().defined().when('enabled', {
        is: true,
        then: (field) => field.notOneOf([NO_PROVIDER],
            translate(ResourcesIds.INVOICING_SETTINGS_PROVIDER_REQUIRED)),
    }),

    apiUser: requiredWhenEnabled(translate, ResourcesIds.INVOICING_SETTINGS_API_USER_REQUIRED),

    /**
     * The one field whose emptiness is ambiguous: blank means "no secret" for a clinic that
     * has never set one and "keep the stored secret" for a clinic that has. The panel never
     * prefills it -- a request that leaves it undefined keeps what is stored, and that
     * absence is the whole mechanism by which saving the VAT rate does not wipe the
     * credentials -- so which of the two it is comes in through `context`.
     */
    // `.optional()`, not `.defined()`: the draft leaves this `undefined` on purpose, and
    // that absence is what tells the API to keep the secret it already has.
    apiSecret: yup.string().optional().when('enabled', {
        is: true,
        then: (field) => field.test('required',
            translate(ResourcesIds.INVOICING_SETTINGS_API_SECRET_REQUIRED),
            function (value): boolean {
                const stored: boolean =
                    (this.options.context as InvoicingValidationContext | undefined)
                        ?.secretIsStored ?? false;

                return stored || !!value?.trim();
            }),
    }),

    companyCif: requiredWhenEnabled(translate,
        ResourcesIds.INVOICING_SETTINGS_COMPANY_CIF_REQUIRED),
    seriesName: requiredWhenEnabled(translate, ResourcesIds.INVOICING_SETTINGS_SERIES_REQUIRED),

    // Asked for only when the clinic collects by receipt, which is a rule about one field
    // given another and so cannot live on the field alone.
    collectSeriesName: yup.string().defined().when(['enabled', 'collectType'], {
        is: (enabled: boolean, collectType: string) =>
            enabled && collectType === RECEIPT_COLLECT_TYPE,
        then: (field) => field.test('required',
            translate(ResourcesIds.INVOICING_SETTINGS_COLLECT_SERIES_REQUIRED),
            (value) => !!value?.trim()),
    }),

    // Checked whether or not the feature is on: it is stored either way, and a blank one
    // used to be the single thing that could block Save on a switched-off panel.
    vatPercentage: yup.string().defined()
        .test('percentage', translate(ResourcesIds.INVOICING_SETTINGS_VAT_INVALID), (value) => {
            const parsed: number = Number(value);
            return !!value?.trim() && !Number.isNaN(parsed) && parsed >= 0 && parsed <= 100;
        }),

    vatName: yup.string().defined(),
    measuringUnit: yup.string().defined(),
    productType: yup.string().defined(),
    language: yup.string().defined(),
    currency: yup.string().defined(),
    pricesIncludeVat: yup.boolean().defined(),
    collectType: yup.string().defined(),
    sendEmailToClient: yup.boolean().defined(),
});
